/**
 * System Parameters API service.
 * Provides CRUD operations for the dynamic parameter config system.
 */
import api from '../config/api';

const PARAMETERS_URL = '/api/v1/parameters';

export async function fetchCategories(): Promise<unknown> {
  const { data } = await api.get(`${PARAMETERS_URL}/categories`);
  return data;
}

export async function fetchParameters(
  category: string | null = null,
  search: string | null = null,
  page = 1,
  pageSize = 50
): Promise<unknown> {
  const params: Record<string, unknown> = { page, page_size: pageSize, active_only: true };
  if (category) params.category = category;
  if (search) params.search = search;
  const { data } = await api.get(PARAMETERS_URL, { params });
  return data;
}

export async function fetchCategoryParams(category: string): Promise<{ parameters?: Record<string, unknown> }> {
  const { data } = await api.get(`${PARAMETERS_URL}/${category}`);
  return data;
}

export async function fetchParameter(category: string, key: string): Promise<unknown> {
  const { data } = await api.get(`${PARAMETERS_URL}/${category}/${encodeURIComponent(key)}`);
  return data;
}

export async function updateParameter(
  category: string,
  key: string,
  value: unknown,
  changeReason: string | null = null
): Promise<unknown> {
  const { data } = await api.put(`${PARAMETERS_URL}/${category}/${encodeURIComponent(key)}`, {
    value,
    change_reason: changeReason,
  });
  return data;
}

export async function resetParameter(category: string, key: string): Promise<unknown> {
  const { data } = await api.post(`${PARAMETERS_URL}/${category}/${encodeURIComponent(key)}/reset`);
  return data;
}

export async function bulkUpdateParameters(
  parameters: unknown[],
  changeReason: string | null = null
): Promise<unknown> {
  const { data } = await api.post(`${PARAMETERS_URL}/bulk`, {
    parameters,
    change_reason: changeReason,
  });
  return data;
}

export async function fetchParameterHistory(
  category: string,
  key: string,
  limit = 50
): Promise<unknown> {
  const { data } = await api.get(`${PARAMETERS_URL}/${category}/${encodeURIComponent(key)}/history`, {
    params: { limit },
  });
  return data;
}

export async function fetchFeatureToggles(): Promise<unknown> {
  const { data } = await api.get(`${PARAMETERS_URL}/ui`);
  return data;
}

export async function invalidateCache(category: string | null = null): Promise<unknown> {
  const params = category ? { category } : {};
  const { data } = await api.post(`${PARAMETERS_URL}/cache/invalidate`, null, { params });
  return data;
}

export async function exportParameters(): Promise<unknown> {
  const { data } = await api.get(`${PARAMETERS_URL}/export`);
  return data;
}

export async function importParameters(
  parameters: unknown,
  overwrite = false
): Promise<unknown> {
  const { data } = await api.post(`${PARAMETERS_URL}/import`, { parameters, overwrite });
  return data;
}
